import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/StudentViewAssignmentFeedback/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton, Stack, styled, Tag, Text } from 'shell/components';
import { FEEDBACK_LIMIT } from 'utils/constants';

const Header = () => {
    const { feedback } = useApp();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const {
        currentFeedback,
        currentFeedbackIndex,
        isGenerating,
        showDetails,
        setShowDetails,
        onClickBack,
        onClickInfo,
        onClickPrevAttempt,
        onClickNextAttempt,
        onClickRegenerate,
    } = useContext(PageContext);

    const canClickPrev = currentFeedbackIndex > 0;
    const canClickNext = currentFeedbackIndex < feedback.length - 1;

    return device === 'mobile' ? (
        <RootStack device={device} justifyContent={'space-between'} showDetails={showDetails} spacing={3}>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Button
                    disabled={isGenerating}
                    startIcon={<Icon kind={'arrowLeft'} />}
                    variant={'borderless'}
                    onClick={onClickBack}
                >
                    {t('common:controls.back', 'Back')}
                </Button>
                <Button variant={'borderless'} onClick={onClickInfo}>
                    {t('feedback-section.feedback-introduction.how-does-it-work', 'How does it work?')}
                </Button>
            </Stack>
            <Stack direction={'row'} justifyContent={'space-between'}>
                <Stack alignItems={'center'} direction={'row'}>
                    <Text variant={'p1'}>Details</Text>
                    <IconButton
                        kind={showDetails ? 'dropdownArrowUp' : 'dropdownArrowDown'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => setShowDetails((current) => !current)}
                    />
                </Stack>
                <Stack direction={'row'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <IconButton
                            aria-label={'previous attempt'}
                            disabled={isGenerating || !canClickPrev}
                            kind={'chevronLeft'}
                            size={'medium'}
                            tooltip={{ component: t('feedback-section.prev-attempt', 'Previous attempt') }}
                            variant={'square'}
                            onClick={onClickPrevAttempt}
                        />
                        <Text>
                            {t('attempt-status', 'Attempt {{attemptStatus}}', {
                                attemptStatus: `${currentFeedbackIndex + 1}/${FEEDBACK_LIMIT}`,
                            })}
                        </Text>
                        <IconButton
                            aria-label={'next attempt'}
                            disabled={isGenerating || !canClickNext}
                            kind={'chevronRight'}
                            size={'medium'}
                            tooltip={{ component: t('feedback-section.next-attempt', 'Next attempt') }}
                            variant={'square'}
                            onClick={onClickNextAttempt}
                        />
                    </Stack>
                    <IconButton
                        aria-label={'regenerate'}
                        disabled={isGenerating}
                        kind={'refresh'}
                        size={'medium'}
                        tooltip={{ component: t('common:controls.regenerate', 'Regenerate') }}
                        variant={'square'}
                        onClick={onClickRegenerate}
                    />
                </Stack>
            </Stack>
            {showDetails && (
                <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Tag type={'draft'} value={1} />
                        <Text color={'light'} variant={'p3'}>
                            {t('common:ui.format-date-time-sign', { val: currentFeedback.created_on })}
                        </Text>
                    </Stack>
                    <Text component={'h1'} noWrap variant={'p1'}>
                        {currentFeedback.submission.name ||
                            t('attempt-status', 'Attempt {{attemptStatus}}', {
                                attemptStatus: currentFeedbackIndex + 1,
                            })}
                    </Text>
                </Stack>
            )}
        </RootStack>
    ) : (
        <RootStack alignItems={'center'} device={device} direction={'row'} spacing={4}>
            <Button
                disabled={isGenerating}
                startIcon={<Icon kind={'arrowLeft'} />}
                variant={'borderless'}
                onClick={onClickBack}
            >
                {t('common:controls.back', 'Back')}
            </Button>
            <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Tag type={'draft'} value={1} />
                    <Text color={'light'} variant={'p3'}>
                        {t('common:ui.format-date-time-sign', { val: currentFeedback.created_on })}
                    </Text>
                </Stack>
                <Text component={'h1'} noWrap variant={'p1'}>
                    {currentFeedback.submission.name ||
                        t('attempt-status', 'Attempt {{attemptStatus}}', {
                            attemptStatus: currentFeedbackIndex + 1,
                        })}
                </Text>
            </Stack>
            <Button variant={'borderless'} onClick={onClickInfo}>
                {t('feedback-section.feedback-introduction.how-does-it-work', 'How does it work?')}
            </Button>
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                <IconButton
                    aria-label={'previous attempt'}
                    disabled={isGenerating || !canClickPrev}
                    kind={'chevronLeft'}
                    size={'medium'}
                    tooltip={{ component: t('feedback-section.prev-attempt', 'Previous attempt') }}
                    variant={'square'}
                    onClick={onClickPrevAttempt}
                />
                <Text>
                    {t('attempt-status', 'Attempt {{attemptStatus}}', {
                        attemptStatus: `${currentFeedbackIndex + 1}/${FEEDBACK_LIMIT}`,
                    })}
                </Text>
                <IconButton
                    aria-label={'next attempt'}
                    disabled={isGenerating || !canClickNext}
                    kind={'chevronRight'}
                    size={'medium'}
                    tooltip={{ component: t('feedback-section.next-attempt', 'Next attempt') }}
                    variant={'square'}
                    onClick={onClickNextAttempt}
                />
            </Stack>
            <IconButton
                aria-label={'regenerate'}
                disabled={isGenerating}
                kind={'refresh'}
                size={'medium'}
                tooltip={{ component: t('common:controls.regenerate', 'Regenerate') }}
                variant={'square'}
                onClick={onClickRegenerate}
            />
        </RootStack>
    );
};

const RootStack = styled(Stack)(({ theme, device, showDetails }) => {
    return {
        position: 'absolute',
        top: 0,
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        width: '100%',
        height: device === 'mobile' ? (showDetails ? '145px' : '89px') : '65px',
        padding: device === 'mobile' ? theme.spacing(5, 4) : theme.spacing(3, 5),
        background: theme.palette.ensightful.shades[0],
    };
});

export default Header;
